
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function ThreePhasePowerPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://electriciansolutionsfresno.com';
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Three-Phase Power Installations',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US'
      }
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California'
    },
    description:
      'Professional three-phase power installation services in Fresno CA for commercial and industrial facilities requiring heavy-duty electrical systems',
    url: `${siteUrl}/services/commercial-specialist/three-phase-power`
  };

  const features = [
    {
      icon: 'ri-flashlight-fill',
      title: 'Three-Phase Service Installation',
      description:
        'Complete three-phase electrical service from utility connection to distribution'
    },
    {
      icon: 'ri-dashboard-line',
      title: 'Three-Phase Panel Installation',
      description:
        'Heavy-duty electrical panels and distribution equipment for three-phase power'
    },
    {
      icon: 'ri-tools-line',
      title: 'Equipment Circuit Installation',
      description:
        'Dedicated three-phase circuits for machinery and industrial equipment'
    },
    {
      icon: 'ri-arrow-left-right-line',
      title: 'Phase Balancing',
      description:
        'Proper load balancing across all three phases for optimal performance'
    },
    {
      icon: 'ri-plug-2-line',
      title: 'Disconnect Installation',
      description:
        'Safety disconnects and overcurrent protection for three-phase equipment'
    },
    {
      icon: 'ri-settings-3-line',
      title: 'Three-Phase Upgrades',
      description:
        'Upgrading existing single-phase systems to three-phase power'
    }
  ];

  return (
    <>
      <SEOHead
        title="Three-Phase Power Installation Fresno | Commercial Electrician CA"
        description="Expert three-phase power installation in Fresno CA. Heavy-duty electrical service for commercial and industrial facilities. Licensed C-10 electrical contractors."
        canonical="/services/commercial-specialist/three-phase-power"
        keywords="three-phase power Fresno, three-phase electrical installation, industrial electrician, commercial power"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Three-Phase Power Installations — Fresno, CA"
        heroSubtitle="Heavy-duty three-phase electrical service for commercial and industrial facilities in Fresno"
        heroImage="https://public.readdy.ai/ai/img_res/08603adcef0be6859e646207d47292da.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Three-phase%20electrical%20meter%20and%20service%20entrance%20equipment%20with%20heavy%20duty%20conduit%20runs%20and%20organized%20wiring%20installation%20on%20exterior%20commercial%20building%20wall&width=700&height=600&seq=threephase-fresno-001&orientation=portrait"
        fresnoContent="Providing reliable three-phase power solutions for Fresno's commercial and industrial facilities. From manufacturing plants to large commercial buildings, restaurants to warehouses throughout Fresno County, we install complete three-phase electrical systems that power heavy-duty equipment and high-demand operations. Our licensed electricians coordinate with PG&E, handle all permitting and inspections, and deliver code-compliant three-phase installations that provide the reliable power your business demands. We understand the critical importance of proper three-phase electrical design and installation for Fresno businesses."
        features={features}
        faqs={[
          {
            question: 'What is three-phase power and why do I need it in Fresno?',
            answer:
              'Three-phase power is a heavy-duty electrical system that provides more power and efficiency than standard single-phase residential service. Fresno businesses need three-phase power for large commercial HVAC systems, industrial machinery and equipment, commercial kitchen equipment (ovens, ranges, dishwashers), heavy-duty motors and compressors, manufacturing equipment, and high-capacity electrical loads. Three-phase power delivers more consistent voltage, handles higher loads more efficiently, reduces energy costs for large equipment, and is required by many commercial and industrial equipment manufacturers. Most commercial and industrial facilities in Fresno require three-phase electrical service.'
          },
          {
            question: 'How much does three-phase power installation cost in Fresno?',
            answer:
              'Three-phase power installation costs in Fresno vary based on distance from existing three-phase service and electrical load requirements. If three-phase power is available at the street, installation typically ranges from $8,000-$25,000 including utility coordination, service entrance equipment, main panel, and initial distribution. If PG&E must extend three-phase lines to your property, costs can range from $25,000-$100,000+ depending on distance. Interior three-phase panel and circuit installation adds $5,000-$20,000+ depending on equipment needs. We provide detailed estimates after site evaluation and utility coordination. Many costs may be offset by PG&E economic development programs for Fresno businesses.'
          },
          {
            question: 'How long does three-phase power installation take in Fresno?',
            answer:
              'Three-phase power installation timelines in Fresno depend on utility availability and project scope. If three-phase service is available at the street, installation typically takes 4-8 weeks including utility coordination, permitting, and installation. If PG&E must extend three-phase lines to your property, the timeline extends to 3-6 months depending on distance and utility workload. We coordinate all aspects including PG&E applications, City of Fresno permits, service entrance installation, and final inspections. Early planning is essential for new construction or business expansions requiring three-phase power in Fresno.'
          },
          {
            question: 'Can you coordinate with PG&E for three-phase service in Fresno?',
            answer:
              'Yes, we handle all PG&E coordination for three-phase power installation in Fresno CA. We submit service applications to PG&E, coordinate site surveys and utility engineering, manage utility construction timelines, ensure proper metering and service entrance equipment, coordinate final utility connection, and handle all required inspections. Our licensed electricians have extensive experience working with PG&E on commercial and industrial electrical projects throughout Fresno. We act as your single point of contact for the entire three-phase installation process, simplifying what can be a complex utility coordination effort.'
          },
          {
            question: 'What equipment requires three-phase power in Fresno businesses?',
            answer:
              'Common equipment requiring three-phase power in Fresno includes large commercial HVAC systems (5+ tons), commercial kitchen equipment (ranges, ovens, dishwashers, hood systems), industrial machinery and manufacturing equipment, large air compressors, commercial elevators, heavy-duty motors over 5 HP, welding equipment, CNC machines and industrial tools, commercial laundry equipment, and high-capacity electrical loads. Equipment manufacturers specify electrical requirements—if equipment requires 208V or 480V three-phase power, you must have three-phase service installed. We review your equipment specifications and design appropriate three-phase electrical systems for Fresno businesses.'
          },
          {
            question: 'Are you licensed for three-phase electrical work in Fresno?',
            answer:
              'Yes, we hold a California C-10 Electrical Contractor license required for all three-phase electrical work in Fresno CA. Our electricians are trained in three-phase power systems, high-voltage installations, load calculations, and California electrical codes. We carry full commercial liability insurance and workers\' compensation coverage. All work is permitted through the City of Fresno Building Department and inspected for code compliance. We coordinate with PG&E engineers and inspectors to ensure proper three-phase service installation. Our team has extensive experience with three-phase electrical systems for commercial and industrial facilities throughout Fresno and Central California.'
          }
        ]}
      />
    </>
  );
}
